/** Small building blocks shared by the screens (markup from the approved mockup). */
import { useEffect, type ReactNode } from "react";
import type { DocView } from "../lib/api";
import { BRAND, coColor, coName, TYPE_COLORS, TYPE_LABELS } from "../lib/brand";
import { avatarColor, initials } from "../lib/format";
import { PEND_LABEL, pendKey, type Perms } from "../lib/access";
import dvIcon from "../assets/brand/dv-icon.svg";

export function TypeBadge({ type, sm }: { type: string; sm?: boolean }) {
  const label = TYPE_LABELS[type as keyof typeof TYPE_LABELS] ?? type;
  return (
    <span className={`tb ${sm ? "sm" : ""}`} style={{ ["--tc" as string]: TYPE_COLORS[type as keyof typeof TYPE_COLORS] ?? "#3E4651" }} title={label}>
      {type}
    </span>
  );
}

export function Avatar({ id, name, size = 24 }: { id: string; name: string; size?: number }) {
  return (
    <span className="av" title={name} style={{ background: avatarColor(id), width: size, height: size, fontSize: Math.round(size * 0.4) }}>
      {initials(name)}
    </span>
  );
}

export function Tile({ code, className = "" }: { code: string; className?: string }) {
  if (code === "ALL") {
    return (
      <span className={`tile ${className}`} title="All my companies">
        <img className="dv-tile" alt="" src={dvIcon} />
      </span>
    );
  }
  const t = BRAND[code];
  return (
    <span className={`tile txt ${className}`} style={{ ["--tc" as string]: t?.ink ?? coColor(code) }} title={coName(code)}>
      {code}
    </span>
  );
}

export function Co({ code, label }: { code: string; label?: ReactNode }) {
  return (
    <span className="co" style={{ ["--cc" as string]: coColor(code) }}>
      <span className="dot" />
      {label ?? coName(code)}
    </span>
  );
}

export function DocNo({ d }: { d: DocView }) {
  return (
    <span className="docno">
      {d.base_number}
      {d.version ? <span className="rev"> v{d.version}</span> : null}
    </span>
  );
}

export function StatusCell({ d, p }: { d: DocView; p: Perms }) {
  const k = pendKey(d);
  const main = d.status === "retired" ? <span className="pill arch">Retired</span>
    : d.status === "archived" ? <span className="pill arch">Archived</span>
    : d.revision ? <span className="pill pub">Published</span>
    : d.scheduled ? <span className="pill info">Approved</span>
    : k ? <span className={`pill ${k}`}>{PEND_LABEL[k]}</span> : <span className="pill draft">Draft</span>;
  let second: ReactNode = null;
  if (d.scheduled) second = <span className="pill sm info">v{d.scheduled.version} from {d.scheduled.effective_on}</span>;
  else if (d.revision && k && p.canSeePending(d.company)) second = <span className={`pill sm ${k}`}>v{d.pending!.proposed_version} · {PEND_LABEL[k]}</span>;
  else if (d.revision && d.update_in_progress) second = <span className="pill sm info">Update in progress</span>;
  return <div className="st">{main}{second}</div>;
}

/** Highlight query words (and their acronym expansions) in a title. */
export function Hl({ text, q, expansions }: { text: string; q: string; expansions: { term: string; expansion: string }[] }) {
  const needles = q.trim().split(/\s+/).filter((n) => n.length >= 2);
  for (const e of expansions) needles.push(e.expansion);
  if (!needles.length) return <>{text}</>;
  const re = new RegExp(`(${needles.sort((a, b) => b.length - a.length).map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "ig");
  const parts = text.split(re);
  return <>{parts.map((part, i) => (i % 2 ? <mark key={i}>{part}</mark> : part))}</>;
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children}
    </div>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return <div className="empty loading">{label}</div>;
}

/** Sticky "not saved yet" bar for pages that stage changes (Team, Rating definitions). */
export function SaveBar({ n, busy, onSave, onUndo }: { n: number; busy: boolean; onSave: () => void; onUndo: () => void }) {
  useEffect(() => {
    if (!n) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [n]);
  if (!n) return null;
  return (
    <div className="savebar" role="status">
      <span><b>{n} change{n === 1 ? "" : "s"}</b> not saved yet. Nothing changes for anyone until you save.</span>
      <button className={`btn btn-accent btn-sm ${busy ? "busy" : ""}`} disabled={busy} onClick={onSave}>Save changes</button>
      <button className="btn btn-ghost btn-sm" disabled={busy} onClick={onUndo}>Undo</button>
    </div>
  );
}
